import { Component, OnDestroy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Event, NavigationEnd, Router, RouterLink } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { CommonService } from '@appShared/services/common.service';
import {
   ConfirmModalComponent,
   ConfirmService,
   ConfirmTemplateDirective
} from '@appShared/components/confirm-modal-and-service';
import { ToastrPosition, ToastrType } from '@appShared/services/toastr.service';
import { IProfile } from '@appShared/interfaces/[Interface-based]/profile.interface';
import { AppFacade } from '@appShared/services/app.facade';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
   styleUrls: ['./nav-menu.component.less'],
   imports: [
      RouterLink,
      AsyncPipe,
      ConfirmTemplateDirective,
      ConfirmModalComponent
   ]
})
export class NavMenuComponent implements OnDestroy {
  userProfile: IProfile;
  isShareholdersRoute: boolean;
  showAdminLink: boolean;
  private _isAdminOnlyMode: boolean;
  isAdminOnlyMode$: Observable<boolean>;
  private _ngDestroyed$ = new Subject();

  constructor(
    private _appFacade: AppFacade,
    private _commonService: CommonService,
    private _confirmService: ConfirmService,
    private _router: Router
  ) {
    _appFacade.siteConfig$
      .pipe(takeUntil(this._ngDestroyed$))
      .subscribe(siteConfig => {
        const userProfile = siteConfig.userProfile;
        const isAdminOnlyMode = siteConfig.isAdminOnlyMode;

        this.showAdminLink =
          userProfile.isAdmin || (!isAdminOnlyMode && userProfile.isOfficer);
      });

    this.isAdminOnlyMode$ = _appFacade.isAdminOnlyMode$.pipe(
      tap(isAdminOnlyMode => (this._isAdminOnlyMode = isAdminOnlyMode))
    );

    _router.events
      .pipe(takeUntil(this._ngDestroyed$))
      .subscribe((routerEvent: Event) => {
        if (routerEvent instanceof NavigationEnd) {
          const url = routerEvent.urlAfterRedirects || routerEvent.url;
          this.isShareholdersRoute = url.includes('shareholders');
        }
      });
  }

  ngOnDestroy(): void {
    this._ngDestroyed$.next(true);
    this._ngDestroyed$.complete();
  }

  toggleOffline(): void {
    const siteOffline = this._isAdminOnlyMode;
    const siteOfflineConfirm = siteOffline
      ? {
          header: 'Bringing Site Back ONLINE',
          display:
            'bring the site back <span class="text-success font-weight-bold">ONLINE</span>...',
          postToastr: {
            msg: 'Site back ONLINE',
            type: ToastrType.success
          }
        }
      : {
          header: 'Taking Site OFFLINE',
          display:
            'take the site <span class="text-error font-weight-bold">OFFLINE</span>...',
          postToastr: {
            msg: 'Site taken OFFLINE',
            type: ToastrType.info
          }
        };

    this._confirmService
      .confirm({
        title: `${siteOfflineConfirm.header}!`,
        message: `You are about to
            ${siteOfflineConfirm.display}
            <p class="mt-2 text-error font-weight-bold">Are you sure?<p>`
      })
      .then(() => {
        this._appFacade.toggleIsAdminOnlyMode(!siteOffline);

        this._commonService.messageUser(
          siteOfflineConfirm.postToastr.msg,
          null,
          siteOfflineConfirm.postToastr.type
        );
      })
      .catch(() => {
        /* Do Nothing */
      });
  }

  goToShareholders(): void {
    this._router.navigate(['shareholders']);
    this._commonService.messageUser(
      'One moment while we load shareholders',
      null,
      ToastrType.info,
      { positionClass: ToastrPosition.topRight }
    );
  }
}
